<template>
  <v-container>
    <v-row>
      <v-col cols="12" md="10" lg="8">
        <v-card>
          <v-card-title>
            <v-icon left color="primary">mdi-video-outline</v-icon>
            Google Meet Rooms
          </v-card-title>
          <v-card-subtitle>
            Auto-generate a stable Google Meet URL for each student whose
            schedule is marked online. Saves admins from pasting links and keeps
            the URL from rotating across calendar event recreations. Mindful
            Minds and Agape are enabled by default.
          </v-card-subtitle>

          <v-card-text v-if="loading" class="text-center py-6">
            <v-progress-circular indeterminate color="primary" />
          </v-card-text>

          <v-card-text v-else-if="config">
            <v-switch
              v-model="config.autoGenerateMeetRooms"
              label="Auto-generate Meet rooms"
              inset
              :hint="
                config.autoGenerateMeetRooms
                  ? 'When an online schedule is saved for a student with no meeting link yet, a Google Meet room will be provisioned automatically.'
                  : 'Admins will need to paste meeting links manually on the schedule form.'
              "
              persistent-hint
            />

            <v-divider class="my-4" />

            <div class="text-subtitle-2 mb-2">
              Google Workspace Service Account
            </div>
            <div class="text-caption mb-3">
              Upload the JSON key file for the service account configured for
              Domain-Wide Delegation in your Google Workspace Admin Console. The
              file should declare
              <code>"type": "service_account"</code> and carry a
              <code>client_email</code> + <code>private_key</code>. The private
              key never leaves the server once uploaded.
            </div>

            <v-alert
              v-if="credentialsStatus && credentialsStatus.configured"
              type="success"
              dense
              text
              border="left"
              icon="mdi-check-circle"
            >
              <div class="text-body-2">
                <strong>Configured.</strong>
                <span v-if="credentialsStatus.clientEmail">
                  Service identity:
                  <code>{{ credentialsStatus.clientEmail }}</code>
                </span>
                <span v-else>
                  (Legacy upload — re-upload to surface the service identity.)
                </span>
              </div>
            </v-alert>
            <v-alert
              v-else
              type="warning"
              dense
              text
              border="left"
              icon="mdi-alert"
            >
              <div class="text-body-2">
                Not configured. Meet auto-generation is set up to use a stub
                until credentials are uploaded — admins can still paste custom
                links on the schedule form as a fallback.
              </div>
            </v-alert>

            <v-file-input
              v-model="credentialsFile"
              accept="application/json,.json"
              label="Service account JSON key file"
              prepend-icon="mdi-key-outline"
              show-size
              :loading="uploadingCredentials"
              :disabled="uploadingCredentials"
              class="mt-3"
              hint="Pick the JSON file you downloaded from Google Cloud Console (IAM → Service accounts → Keys → Add key)."
              persistent-hint
            />
            <div class="d-flex justify-end mt-2">
              <v-btn
                color="primary"
                :loading="uploadingCredentials"
                :disabled="!credentialsFile || uploadingCredentials"
                @click="uploadCredentials"
              >
                Upload credentials
              </v-btn>
            </div>

            <v-divider class="my-4" />

            <v-alert
              v-if="config.autoGenerateMeetRooms"
              type="info"
              dense
              text
              border="left"
              class="mt-2"
            >
              <div class="text-body-2">
                Auto-generation requires Google Workspace Domain-Wide Delegation
                to be set up against the tenant's service account (in Admin
                Console → Security → API Controls). Without it, schedule saves
                will still succeed but the meeting link won't be populated —
                admins can paste a custom link as a fallback.
              </div>
            </v-alert>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn text @click="reload" :disabled="saving">Discard</v-btn>
            <v-btn
              color="primary"
              :loading="saving"
              :disabled="!config"
              @click="save"
            >
              Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import {
  getAccount,
  getMeetConfig,
  updateMeetConfig,
  getGoogleCredentialsStatus,
  uploadGoogleCredentials,
} from "@/services/accountServices";

/**
 * Phase 5 + follow-up — per-tenant Meet auto-generation configuration
 * AND the Google Workspace service-account credentials upload that
 * the real Meet API path will eventually consume (currently a stub
 * client; see MeetRoomClient.java header for the swap checklist).
 */
export default {
  name: "MeetSettings",
  data: () => ({
    loading: true,
    saving: false,
    uploadingCredentials: false,
    accountId: null,
    config: null,
    credentialsStatus: null,
    credentialsFile: null,
  }),
  async mounted() {
    await this.reload();
  },
  methods: {
    async reload() {
      this.loading = true;
      try {
        const account = await getAccount();
        this.accountId = account.id;
        const [config, status] = await Promise.all([
          getMeetConfig(this.accountId),
          getGoogleCredentialsStatus(this.accountId),
        ]);
        this.config = config;
        this.credentialsStatus = status;
      } catch (e) {
        const msg =
          e?.response?.data?.message ||
          e?.message ||
          "Failed to load Meet settings";
        this.$store.dispatch("messages/addErrorMessage", msg);
      } finally {
        this.loading = false;
      }
    },
    async save() {
      this.saving = true;
      try {
        await updateMeetConfig(this.accountId, this.config);
        this.$store.dispatch(
          "messages/addSuccessMessage",
          "Meet settings saved",
        );
        await this.reload();
      } catch (e) {
        const msg =
          e?.response?.data?.message ||
          e?.message ||
          "Failed to save Meet settings";
        this.$store.dispatch("messages/addErrorMessage", msg);
      } finally {
        this.saving = false;
      }
    },
    async uploadCredentials() {
      if (!this.credentialsFile) return;
      this.uploadingCredentials = true;
      try {
        // Read the file as text on the client; we don't actually parse
        // the JSON here — the backend validates shape + persists
        // atomically, and surfaces a clear error if the upload isn't a
        // service-account JSON.
        const text = await this.credentialsFile.text();
        const status = await uploadGoogleCredentials(this.accountId, text);
        this.credentialsStatus = status;
        this.credentialsFile = null;
        this.$store.dispatch(
          "messages/addSuccessMessage",
          "Google credentials uploaded",
        );
      } catch (e) {
        const msg =
          e?.response?.data?.message ||
          e?.message ||
          "Failed to upload Google credentials";
        this.$store.dispatch("messages/addErrorMessage", msg);
      } finally {
        this.uploadingCredentials = false;
      }
    },
  },
};
</script>
